import { FC, ReactNode } from "react";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import { BulkSelectionControl } from "@design-system/BulkSelectionControl/BulkSelectionControl";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { Pagination } from "@design-system/Pagination/Pagination";
import { HStack } from "@design-system/HStack/HStack";
import { LineItemsFooterStats } from "@components/lineItems/LineItemsFooterStats";

interface LineItemsFooterStatsData {
    totalOpenAmount: number;
    totalPaidAmount: number;
    totalAmount: number;
    count: number;
    countLabel: string;
}

interface LineItemsCommandIslandFooterProps {
    totalLabel: ReactNode;
    totalPages: number;
    currentPage: number;
    onPageChange: (page: number) => void;
    selectedCount: number;
    hasSelection: boolean;
    hasSelectedByCurrentFilters: boolean;
    onClearSelection: () => void;
    onSelectAllByFilters: () => void;
    onOpenBulkActions: () => void;
    stats?: LineItemsFooterStatsData;
}

export const LineItemsCommandIslandFooter: FC<
    LineItemsCommandIslandFooterProps
> = ({
    totalLabel,
    totalPages,
    currentPage,
    onPageChange,
    selectedCount,
    hasSelection,
    hasSelectedByCurrentFilters,
    onClearSelection,
    onSelectAllByFilters,
    onOpenBulkActions,
    stats,
}) => (
    <CommandIsland
        sticky
        className={commandIslandStyles.bulkActionsStickyFooter}
    >
        <HStack gap={8}>
            {totalPages > 1 && (
                <Pagination
                    pageCount={totalPages}
                    currentPage={currentPage}
                    onPageChange={onPageChange}
                    size="md"
                />
            )}

            {stats ? (
                <LineItemsFooterStats {...stats} />
            ) : (
                totalPages <= 1 && (
                    <CommandIslandTotal>{totalLabel}</CommandIslandTotal>
                )
            )}

            {hasSelection && (
                <>
                    <BulkSelectionControl
                        selectedCount={selectedCount}
                        onClearSelection={onClearSelection}
                        size="md"
                    />
                    {!hasSelectedByCurrentFilters && (
                        <Button
                            variant="primary"
                            size="md"
                            onClick={onSelectAllByFilters}
                        >
                            Select all
                        </Button>
                    )}
                </>
            )}
        </HStack>

        {hasSelection && (
            <Tooltip content="Open bulk actions">
                <Button
                    variant="primary"
                    size="md"
                    onClick={onOpenBulkActions}
                    rightIcon={
                        <span data-slot-box="">
                            <Shortcut keys="mod+k" />
                        </span>
                    }
                >
                    Actions
                </Button>
            </Tooltip>
        )}
    </CommandIsland>
);
